import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ReminderApi, ReminderDTO } from './ReminderApi';

export type FormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: FormMode;
}

const defaultReminder: ReminderDTO = {
  tenantId: '',
  name: '',
  description: '',
  startTime: '',
  endTime: '',
  active: true,
  frequency: 'DAILY',
  channels: ['EMAIL'],
  userIds: [],
  entityType: '',
  entityId: '',
  specificDaysOfWeek: [],
  specificDaysOfMonth: [],
  interval: 1,
  nextReminderTime: '',
};

export const ReminderForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [reminder, setReminder] = useState<ReminderDTO>(defaultReminder);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isReadOnly = mode === 'view';

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user?.tenantId || '';
  const userId = user?.id || 0;

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && id) {
      loadReminder(id);
    } else if (mode === 'create') {
      setReminder(prev => ({ ...prev, tenantId }));
    }
  }, [id, mode]);

  const loadReminder = async (reminderId: string) => {
    setLoading(true);
    try {
      const response = await ReminderApi.getById(reminderId);
      setReminder(response.data);
    } catch (err) {
      setError('Failed to load reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setReminder(prev => ({ ...prev, [name]: newValue }));
  };

  const handleChannelsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setReminder(prev => {
      const channels = prev.channels || [];
      return {
        ...prev,
        channels: checked
          ? [...channels, value]
          : channels.filter(c => c !== value),
      };
    });
  };

  const handleUserIdsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      setReminder(prev => ({
        ...prev,
        userIds: Array.from(new Set([...prev.userIds, value])),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (mode === 'edit' && id) {
        await ReminderApi.update(id, reminder);
        alert('Reminder updated!');
      } else if (mode === 'create') {
        await ReminderApi.create(reminder);
        alert('Reminder created!');
        navigate(-1);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save reminder.');
    }
  };

  const renderInput = (
    label: string,
    field: keyof ReminderDTO,
    type: string = 'text'
  ) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type={type}
        name={field}
        value={(reminder[field] as any) || ''}
        onChange={handleChange}
        readOnly={isReadOnly}
        className={`w-full border px-3 py-2 rounded ${isReadOnly ? 'bg-gray-100' : ''}`}
      />
    </div>
  );

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 mx-auto bg-white rounded shadow max-w-4xl">
      <h2 className="text-xl font-semibold mb-4">{mode === 'create' ? 'Create' : mode === 'edit' ? 'Edit' : 'View'} Reminder</h2>
      {error && <div className="mb-4 text-red-600">{error}</div>}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderInput('Name', 'name')}
        {renderInput('Entity Type', 'entityType')}
        {renderInput('Entity ID', 'entityId')}
        {renderInput('Start Time', 'startTime', 'datetime-local')}
        {renderInput('End Time', 'endTime', 'datetime-local')}
        {renderInput('Frequency', 'frequency')}
        {renderInput('Interval', 'interval', 'number')}
        <div>
          <label className="block text-sm font-medium mb-1">Active</label>
          <input
            type="checkbox"
            name="active"
            checked={reminder.active}
            onChange={handleChange}
            disabled={isReadOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Channels</label>
          {['EMAIL', 'SMS', 'PUSH'].map(channel => (
            <label key={channel} className="block text-sm">
              <input
                type="checkbox"
                value={channel}
                checked={reminder.channels.includes(channel)}
                onChange={handleChannelsChange}
                disabled={isReadOnly}
                className="mr-2"
              />
              {channel}
            </label>
          ))}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">User IDs (comma separated)</label>
          <input
            type="text"
            value={reminder.userIds.join(',')}
            onChange={e =>
              setReminder(prev => ({
                ...prev,
                userIds: e.target.value
                  .split(',')
                  .map(val => parseInt(val.trim()))
                  .filter(n => !isNaN(n)),
              }))
            }
            readOnly={isReadOnly}
            className={`w-full border px-3 py-2 rounded ${isReadOnly ? 'bg-gray-100' : ''}`}
          />
        </div>
        {renderInput('Next Reminder Time', 'nextReminderTime', 'datetime-local')}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            name="description"
            value={reminder.description}
            onChange={handleChange}
            readOnly={isReadOnly}
            className={`w-full border px-3 py-2 rounded ${isReadOnly ? 'bg-gray-100' : ''}`}
            rows={4}
          />
        </div>

        {mode !== 'view' && (
          <div className="md:col-span-2 flex gap-4">
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              {mode === 'create' ? 'Create' : 'Update'}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="bg-gray-500 text-white px-4 py-2 rounded">
              Cancel
            </button>
          </div>
        )}
        {mode === 'view' && (
          <div className="md:col-span-2">
            <button type="button" onClick={() => navigate(-1)} className="bg-red-500 text-white px-4 py-2 rounded">
              Back
            </button>
          </div>
        )}
      </form>
    </div>
  );
};